/* eslint-disable camelcase */
import { GenderAppearanceEnum, GenderIdentityEnum } from '@minecraft.graphql-operations';
import { gqlExternalAuthenticationProvider, gqlGenderAppearance, gqlGenderIdentity } from '@minecraft.graphql-types';
import { parseJwt } from '@minecraft.utils';
import { getAccessToken } from '@blocs.auth';
import { publishEvent } from './publish-event';

interface SignUpProfileCreatedEventInput {
  account: {
    id: number;
    email: string;
    language?: { locale: string };
  };
  artist: {
    artistId: number;
  };
  profile: {
    stageName: string;
    playableAgeRange?: {
      ageMin: number;
      ageMax: number;
    };
    workingLocations?: Array<{ id: string }>;
    ethnicAppearance?: Array<{ id: string }>;
    hasPhotoUpload: boolean;
    gender?: {
      genderAppearanceIds?: (string | number)[];
      genderIdentityIds?: (string | number)[];
    };
  };
  genderAppearances: Array<{ id: string | number; code: GenderAppearanceEnum } | gqlGenderAppearance>;
  genderIdentities: Array<{ id: string | number; code: GenderIdentityEnum } | gqlGenderIdentity>;
  locations: Array<{ id: string | number; name: string }>;
  ethnicAppearances: Array<{ id: string | number; code: string }>;
}

export function publishSignUpProfileCreatedEvent({
  account,
  artist,
  profile,
  genderAppearances,
  genderIdentities,
  locations,
  ethnicAppearances,
}: SignUpProfileCreatedEventInput) {
  const { gender, stageName, playableAgeRange, workingLocations, ethnicAppearance, hasPhotoUpload } = profile;

  const profileGenderAppearance = genderAppearances?.filter((gA) => gender.genderAppearanceIds.includes(String(gA.id)));
  const profileGenderIdentities = genderIdentities?.filter((gA) => gender.genderIdentityIds.includes(String(gA.id)));
  const isGoogleSSO =
    parseJwt(getAccessToken())?.loginAttributes?.externalAuthProvider === gqlExternalAuthenticationProvider.GOOGLE;

  const event = {
    event: 'initial_profile_creation',
    method: isGoogleSSO ? 'google_sso' : 'email',
    user_id: account.id,
    user_type: 'talent',
    locale: navigator.language,
    email: account.email,
    artist_id: artist.artistId,
    stage_name: stageName,
    market: locations?.find((location) => String(location.id) === workingLocations?.[0].id)?.name,
    playable_age_range_low: playableAgeRange?.ageMin,
    playable_age_range_high: playableAgeRange?.ageMax,
    ethnicity: ethnicAppearances?.find((ethnicity) => String(ethnicity.id) === String(ethnicAppearance[0].id))?.code,
    gender_appearance: profileGenderAppearance?.map((ga) => ga.code).join(','),
    gender_identity: profileGenderIdentities?.map((gi) => gi.code).join(','),
    photo_upload: hasPhotoUpload,
  };

  publishEvent(event);
}
